/**
 * Multi-chat table for the lifecoach (yanamas) shell — 2026-08-25.
 *
 * The engine writes a gist (title / one-liner / paragraph summary) for every
 * conversation on every turn; saas persists it on the conversations row. This
 * panel is the picker: a paginated table sortable by last-updated / created /
 * last-visited / title, one line per chat with the one-liner inline and the
 * paragraph summary on hover. Selecting a row stamps last_visited server-side
 * and hands the chat_id back to HortonChat, which switches the live thread.
 *
 * Rows that predate chat switching carry no chat_id yet (it is stamped on a
 * conversation's first gisted turn) — they are listed but not clickable until
 * their next message backfills the id.
 */
import { useEffect, useState } from 'react'
import { X, Plus, Loader2, MessageSquare, ChevronLeft, ChevronRight } from 'lucide-react'

export interface LifecoachChatRow {
  id: string
  chat_id: string | null
  title: string | null
  one_liner: string | null
  summary: string | null
  created_at: string | null
  updated_at: string | null
  last_visited_at: string | null
  message_count: number
}

export type LifecoachChatSort = 'updated' | 'created' | 'visited' | 'title'

interface Props {
  open: boolean
  activeChatId: string | null
  onClose: () => void
  onSelect: (chatId: string) => void
  onNewChat: () => void
}

const SORTS: Array<{ value: LifecoachChatSort; label: string }> = [
  { value: 'updated', label: 'Last updated' },
  { value: 'visited', label: 'Last visited' },
  { value: 'created', label: 'Created' },
  { value: 'title', label: 'Title' },
]

const PER_PAGE = 15

function fmt(ts: string | null): string {
  if (!ts) return '—'
  const d = new Date(ts)
  if (isNaN(d.getTime())) return '—'
  return d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
}

export function LifecoachChatsPanel({ open, activeChatId, onClose, onSelect, onNewChat }: Props) {
  const [rows, setRows] = useState<LifecoachChatRow[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [sort, setSort] = useState<LifecoachChatSort>('updated')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setLoading(true)
    setError(null)
    fetch(`/api/horton-v3/chats?page=${page}&per_page=${PER_PAGE}&sort=${sort}`, {
      credentials: 'include',
    })
      .then(async (r) => {
        if (!r.ok) throw new Error(`chats list failed (${r.status})`)
        return r.json()
      })
      .then((data) => {
        if (cancelled) return
        setRows(Array.isArray(data.rows) ? data.rows : [])
        setTotal(typeof data.total === 'number' ? data.total : 0)
      })
      .catch((e) => { if (!cancelled) setError(e?.message || 'Could not load chats') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [open, page, sort])

  if (!open) return null

  const pages = Math.max(1, Math.ceil(total / PER_PAGE))

  const pick = (row: LifecoachChatRow) => {
    if (!row.chat_id) return
    // Stamp last-visited; the table is already ordered server-side, so this
    // is fire-and-forget — a failed stamp only skews the "visited" sort.
    fetch(`/api/horton-v3/chats/${encodeURIComponent(row.id)}/visited`, {
      method: 'POST',
      credentials: 'include',
    }).catch(() => {})
    onSelect(row.chat_id)
  }

  return (
    <div className="fixed right-0 top-16 w-full sm:w-[26rem] h-[calc(100vh-4rem)] bg-surface border-l border-soft shadow-2xl z-[100] overflow-y-auto">
      <div className="p-4">
        <div className="flex items-center justify-between mb-3 gap-2">
          <h3 className="font-semibold text-primary">Your chats</h3>
          <div className="flex items-center gap-2">
            <button
              onClick={onNewChat}
              className="flex items-center gap-1 text-xs font-medium text-brand border border-strong rounded-lg px-2 py-1.5 hover:bg-surface-alt transition-colors min-h-9"
            >
              <Plus className="w-3.5 h-3.5" />
              New chat
            </button>
            <button
              onClick={onClose}
              className="p-1 hover:bg-surface-alt rounded transition-colors"
              aria-label="Close chats"
            >
              <X className="w-4 h-4 text-muted" />
            </button>
          </div>
        </div>

        <div className="flex items-center justify-between mb-3 gap-2">
          <label className="text-xs text-muted flex items-center gap-1.5">
            Sort by
            <select
              value={sort}
              onChange={(e) => { setSort(e.target.value as LifecoachChatSort); setPage(1) }}
              className="text-xs border border-strong rounded-lg px-2 py-1 bg-surface text-secondary"
            >
              {SORTS.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </label>
          <span className="text-xs text-muted">{total} chat{total === 1 ? '' : 's'}</span>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-10 text-muted">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : error ? (
          <div className="text-center py-8 text-sm text-bad">{error}</div>
        ) : rows.length === 0 ? (
          <div className="text-center py-8">
            <MessageSquare className="w-12 h-12 text-muted mx-auto mb-3 opacity-50" />
            <p className="text-sm text-muted">No chats yet</p>
            <p className="text-xs text-muted mt-2">Start a conversation and it will appear here</p>
          </div>
        ) : (
          <div className="space-y-2">
            {rows.map((row) => {
              const isActive = !!row.chat_id && row.chat_id === activeChatId
              const clickable = !!row.chat_id && !isActive
              return (
                <div
                  key={row.id}
                  // Native tooltip carries the paragraph summary on hover.
                  title={row.summary || undefined}
                  onClick={() => { if (clickable) pick(row) }}
                  className={`p-3 border rounded-lg transition-colors ${
                    isActive
                      ? 'border-brand/50 bg-brand/5'
                      : clickable
                        ? 'border-soft hover:bg-surface-alt cursor-pointer'
                        : 'border-soft opacity-60'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm text-primary flex-1 truncate">
                      {row.title || 'Untitled chat'}
                    </span>
                    {isActive && (
                      <span className="px-2 py-0.5 text-xs font-medium bg-brand/15 text-brand rounded-full flex-shrink-0">
                        Open
                      </span>
                    )}
                  </div>
                  {row.one_liner && (
                    <div className="text-xs text-secondary mt-1 line-clamp-2">{row.one_liner}</div>
                  )}
                  <div className="text-app-micro text-muted mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                    <span>{row.message_count} message{row.message_count === 1 ? '' : 's'}</span>
                    <span>Created {fmt(row.created_at)}</span>
                    <span>Updated {fmt(row.updated_at)}</span>
                    {row.last_visited_at && <span>Visited {fmt(row.last_visited_at)}</span>}
                  </div>
                  {!row.chat_id && (
                    <div className="text-app-micro text-muted mt-1 italic">
                      Becomes switchable after its next message
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}

        {pages > 1 && (
          <div className="flex items-center justify-between mt-3">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="flex items-center gap-1 text-xs text-secondary border border-strong rounded-lg px-2 py-1.5 disabled:opacity-40 hover:bg-surface-alt transition-colors"
            >
              <ChevronLeft className="w-3.5 h-3.5" /> Prev
            </button>
            <span className="text-xs text-muted">Page {page} of {pages}</span>
            <button
              onClick={() => setPage((p) => Math.min(pages, p + 1))}
              disabled={page >= pages}
              className="flex items-center gap-1 text-xs text-secondary border border-strong rounded-lg px-2 py-1.5 disabled:opacity-40 hover:bg-surface-alt transition-colors"
            >
              Next <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
